import type { SortOption } from '@/shared/types';

import type { FilterState } from '@/entities/discovery';

/**
 * @description 자산 관련 쿼리 키 모음
 * dataAssetsPrefix: 자산 목록 조회 초기화 키
 * dataAssets: 자산 목록
 * assetBaseInfoPrefix: 자산 정보 조회 초기화 키
 * assetBaseInfo: 자산 정보 조회 키
 * schema: 자산 스키마 정보 조회 키
 * lineage: 자산 계보 정보 조회 키
 */

export const assetKeys = {
  all: ['assets'] as const,

  dataAssetsPrefix: () => [...assetKeys.all, 'dataAssets'] as const,
  dataAssets: (
    domainFqn: string,
    filters: FilterState,
    query: string,
    sortBy: SortOption['field'],
    sortOrder: SortOption['direction']
  ) => [...assetKeys.all, 'dataAssets', domainFqn, filters, query, sortBy, sortOrder] as const,

  assetBaseInfoPrefix: () => [...assetKeys.all, 'assetBaseInfo'] as const,
  assetBaseInfo: (entityType: string, fqn: string) => [...assetKeys.all, 'assetBaseInfo', entityType, fqn] as const,

  schemaPrefix: () => [...assetKeys.all, 'schema'] as const,
  schema: (entityType: string, fqn: string) => [...assetKeys.all, 'schema', entityType, fqn] as const,

  lineagePrefix: () => [...assetKeys.all, 'lineage'] as const,
  lineage: (entityType: string, fqn: string) => [...assetKeys.all, 'lineage', entityType, fqn] as const,

  tableColumnsCount: (tableFqn: string, includeDeleted: boolean) =>
    [...assetKeys.all, 'tableColumnsCount', tableFqn, includeDeleted] as const,

  sampleDataPrefix: () => [...assetKeys.all, 'sampleData'] as const,
  sampleData: (entityType: string, entityId: string) =>
    [...assetKeys.sampleDataPrefix(), entityType, entityId] as const,

  databaseTablesPrefix: () => [...assetKeys.all, 'databaseTables'] as const,
  databaseTables: (databaseSchemaFqn: string, includeDeleted: boolean) =>
    [...assetKeys.databaseTablesPrefix(), databaseSchemaFqn, includeDeleted] as const,

  serviceMLModelsPrefix: () => [...assetKeys.all, 'serviceMLModels'] as const,
  serviceMLModels: (serviceName: string, includeDeleted: boolean) =>
    [...assetKeys.serviceMLModelsPrefix(), serviceName, includeDeleted] as const,

  serviceDatabasesPrefix: () => [...assetKeys.all, 'serviceDatabases'] as const,
  serviceDatabases: (serviceName: string, includeDeleted: boolean) =>
    [...assetKeys.serviceDatabasesPrefix(), serviceName, includeDeleted] as const,

  serviceContainersPrefix: () => [...assetKeys.all, 'serviceContainers'] as const,
  serviceContainers: (serviceName: string, includeDeleted: boolean) =>
    [...assetKeys.serviceContainersPrefix(), serviceName, includeDeleted] as const,

  databaseSchemasPrefix: () => [...assetKeys.all, 'databaseSchemas'] as const,
  databaseSchemas: (databaseFqn: string, includeDeleted: boolean) =>
    [...assetKeys.databaseSchemasPrefix(), databaseFqn, includeDeleted] as const,

  containerFilesPrefix: () => [...assetKeys.all, 'containerFiles'] as const,
  containerFiles: (containerFqn: string, path: string) =>
    [...assetKeys.containerFilesPrefix(), containerFqn, path] as const,

  columnsPrefix: () => [...assetKeys.all, 'columns'] as const,
  columns: (fullyQualifiedName: string) => [...assetKeys.columnsPrefix(), fullyQualifiedName] as const,

  schemaTablesCountPrefix: () => [...assetKeys.all, 'schemaTablesCount'] as const,
  schemaTablesCount: (schemaFqn: string) => [...assetKeys.schemaTablesCountPrefix(), schemaFqn] as const,

  availableTiersPrefix: () => [...assetKeys.all, 'availableTiers'] as const,
  availableTiers: () => [...assetKeys.availableTiersPrefix()] as const,

  availableCertificationsPrefix: () => [...assetKeys.all, 'availableCertifications'] as const,
  availableCertifications: () => [...assetKeys.availableCertificationsPrefix()] as const,
} as const;
